import { faDownload } from '@fortawesome/pro-regular-svg-icons';
import React, { useCallback, useState } from 'react';
import DataTableToolbarButton from '../../../../voxel/DataTableToolbar/DataTableToolbarButton';
import { ProjectGq } from '../../../../../graphql/projects/models/ProjectGq';
import { useApolloClient } from '@apollo/client';
import { projectsApiService } from '../../../../../graphql/projects/projectsApiService';
import {
  CommandGq,
  DataGq,
  VariablesGq,
  gql_query,
} from '../../../../../graphql/projects/queries/useGetKeywordDownloadGq';

import { KeywordTableState } from '../logic/useKeywordTableState';

export interface DownloadButtonProps {
  project: ProjectGq;
  tableState: KeywordTableState;
  onDownload?: () => unknown;
  selectedCount?: number;
  selectionActive?: boolean;
}

export const DownloadButton: React.FC<DownloadButtonProps> = ({
  project,
  tableState,
  onDownload,
  selectedCount,
  selectionActive
}) => {
  const exportMutation = projectsApiService.mutations.projects.useExport();
  const downloadsListQuery = projectsApiService.queries.useGetKeywordDownloads(
    { projectId: project?.id ?? 0 },
    { skip: !project?.id }
  );
  const apolloClient = useApolloClient();
  const getDownload = useCallback(
    (command: CommandGq) =>
      apolloClient.query<DataGq, VariablesGq>({
        query: gql_query,
        variables: {
          command,
        },
      }),
    []
  );

  const [downloading, setDownloading] = useState(false);
  const handleExportToExcel = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      if (!project?.id) return;

      // Close the selection instantly
      tableState.handleResetSelection();

      // Start with the mutation
      setDownloading(true);
      exportMutation({
        keywordQuery: {
          selecting: {
            ...tableState.backendSelectionState,
            emptyIsAll: true,
          },
          filtering: tableState.backendFilterState,
        },
        includeMonths: true,
        projectId: project.id ?? 0,
      })
        .then(async (download) => {
          // Refresh entire downloads list
          await downloadsListQuery.query.refetch();

          // Wait a bit before showing menu (allowing for data to inject into component)
          await new Promise((resolve, reject) => setTimeout(resolve, 250));
          onDownload && onDownload();

          // Poll database for download URL
          let downloadUrl = download.downloadUrl;

          while (!downloadUrl) {
            // Get data again
            let refreshedDownload = await getDownload({
              downloadId: download.id,
              projectId: project.id,
            });
            downloadUrl =
              refreshedDownload?.data?.projects?.getKeywordDownload?.download?.downloadUrl;

            // Wait a bit
            await new Promise((resolve, reject) => setTimeout(resolve, 500));
          }
          return downloadUrl;
        })
        .then((downloadUrl) => {
          window.location.href = downloadUrl;
        })
        .catch((error) => {
          console.error(error);
        })
        .finally(() => setDownloading(false));
    },
    [true, tableState.selectionState, tableState.filterState, tableState.sortingState, project?.id]
  );

  return (
    <DataTableToolbarButton
      tooltip={'Download selected keywords'}
      iconProp={faDownload}
      onClick={(event) => handleExportToExcel(event)}
      disabled={downloading}
      loading={downloading}
      effect={selectionActive ? 'primary' : undefined}
      effectAnimate={false}
    />
  );
};

export default DownloadButton;
